import { FormItem } from "./FormItem";

/**
 * MultiCheck (ft "mchk") — groupe de checkboxes multi-choix (HA-75).
 *
 * Pendant multi-valeur du Dropdown : le converter mockup collapse un groupe
 * d'`<input type=checkbox>` partageant un `name` en `[]` en UN nœud formi qui
 * porte ses options dans `choices` (JSON `[{k, v}]` — même contrat que le
 * dropdown, donc hydratable côté serveur par assignChoices sans rien changer).
 * L'item re-rend de VRAIES checkboxes natives ; `getValue()` retourne le
 * TABLEAU des valeurs cochées — FormController append chaque valeur sous le
 * name `…[]`, PHP re-matérialise le tableau nativement.
 *
 * Ne pas confondre avec `checkbox` (sélecteur modal legacy, CheckboxController)
 * ni `bool` (checkbox unique oui/non sans valeur).
 */
export class MultiCheck extends FormItem {

    public getType(): string {
        return "MultiCheck";
    }

    public getValue(): any {
        return this.checkboxes()
            .filter((input) => input.checked)
            .map((input) => input.value);
    }

    public reset(): void {
        this.setState("idle");
        this.checkboxes().forEach((input) => { input.checked = false; });
    }

    public validate(): boolean {
        const rules = this.getValidationRules();
        if (!rules) { return true; }
        if (rules.required && (this.getValue() as string[]).length === 0) {
            this.setState("error", rules.requiredMsg || "Choisissez au moins une option");
            return false;
        }
        this.setState("idle");
        return true;
    }

    protected createSelf(): void {
        const choices = this.parseList(this.get("choices"));
        // Pré-cochage : `value` = JSON `["k1", …]` (converter : inputs [checked]
        // du mockup ; serveur : assignFormField avec un JSON de sélection).
        const selected = new Set(this.parseList(this.trans("value")).map((v: any) => String(v?.k ?? v)));
        const iclass = this.get("iclass");
        const lclass = this.get("lclass");

        const items: any[] = [];
        for (const c of choices) {
            const value = String(c.k ?? c.v ?? c);
            const label = String(c.v ?? c.k ?? c);
            const inputAttrs: any[] = [
                {n: "type", v: "checkbox"},
                {n: "value", v: value},
            ];
            if (selected.has(value)) {
                inputAttrs.push({n: "checked", v: ""});
            }
            items.push({
                _tn: "label",
                attributes: [],
                c: [
                    {t: "t", _tn: "input", attributes: inputAttrs, class: iclass || undefined},
                    {t: "t", _tn: "span", txt: label},
                ],
                class: lclass || "mchk-item",
                t: "t",
            });
        }

        // Les attrs moteur ([ft]/[name]) vont sur le conteneur du groupe : c'est
        // le "contrôle réel" au sens du renderer bare (createItemTag y ajoute
        // les attrs [info] — n/validate/choices — via renderWrapper).
        const group = this.createItemTag({
            _tn: "div",
            attributes: [
                {n: "ft",   v: "mchk"},
                {n: "name", v: this.get("name")},
                {n: "role", v: "group"},
            ],
            c: items,
            class: "mchk-group",
            t: "t",
        });
        this.setElement(group.getElement());
    }

    private checkboxes(): HTMLInputElement[] {
        const el = this.getElement() as HTMLElement | null;
        if (!el || !el.querySelectorAll) { return []; }
        return Array.from(el.querySelectorAll("input[type='checkbox']")) as HTMLInputElement[];
    }

    /** Tolère tableau natif, JSON string, ou CSV (prefill manuel). */
    private parseList(raw: any): any[] {
        if (raw === null || raw === undefined || raw === "" || raw === "undefined") {
            return [];
        }
        if (Array.isArray(raw)) {
            return raw;
        }
        if (typeof raw === "string") {
            try {
                const parsed = JSON.parse(raw);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return raw.split(",").map((s) => s.trim()).filter((s) => s !== "");
            }
        }
        return [];
    }
}
